import React, { useEffect, useState, useContext, useCallback } from 'react';
import { Grid } from '@mui/material';
import Loading from "../../components/Loading/Loading";
import { getArticulosByCategory } from "../../services/ArticulosServices";
import { getCarritoByID } from "../../services/CarritoServices";
import { CategoryFilter } from '../../components/CategoryFilter/CategoryFilter';
import { ProductList } from '../../components/ProductList/ProductList';
import ControlsRow from '../../components/ControlsRows/ControlsRows';
import { pdf } from '@react-pdf/renderer';
import ExportPdf from '../../components/pdf/exportPdf';
import { AuthContext } from "../../context/authContext";
import logoDefault from "../../images/productDefault.jpg";
import { useFilterParams } from '../../hooks/useFilterParams';

const getOrder = (ob) => {
  switch (ob) {
    case "precio_asc": return { orderBy: 'precioMostrar', orden: 'asc' };
    case "precio_desc": return { orderBy: 'precioMostrar', orden: 'desc' };
    case "nombre_asc": return { orderBy: 'art_descripcion', orden: 'asc' };
    case "nombre_desc": return { orderBy: 'art_descripcion', orden: 'desc' };
    case "codigo_asc": return { orderBy: 'art_codigo', orden: 'asc' };
    case "codigo_desc": return { orderBy: 'art_codigo', orden: 'desc' };
    default: return { orderBy: '', orden: '' };
  }
};

const CategoryResults = () => {
  const { empresa, user, ramos = [], rubros = [], subrubros = [], marcas = [] } = useContext(AuthContext);

  const {
    searchTerm,
    selectedRamoId, selectedRubroId, selectedSubrubroId, selectedMarcaId,
    currentPage, perPage, orderBy,
    setRamoId, setRubroId, setSubrubroId, setMarcaId,
    setSearch, setCurrentPage, setPerPage, setOrderBy,
    clearAll,
  } = useFilterParams();

  const [products, setProducts] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [viewMode, setViewMode] = useState('horizontal');
  const [generandoPDF, setGenerandoPDF] = useState(false);
  const [formatoPDF, setFormatoPDF] = useState();

  useEffect(() => {
    setViewMode(empresa?.vista === false ? 'horizontal' : 'grid');
  }, [empresa]);

  useEffect(() => { fetchCarrito(); }, []);

  const fetchCarrito = async () => {
    const idCarrito = localStorage.getItem('idCarrito');
    if (!idCarrito) return;
    try {
      const res = await getCarritoByID(idCarrito);
      if (res?.Carrito_items) {
        setCartItems(res.Carrito_items.map(item => ({
          cartId: res.id, itemId: item.id, artId: item.art_Id,
        })));
      }
    } catch (e) { console.error(e); }
  };

  const getImageSrc = useCallback((producto) => {
    const a = producto?.art_imagen?.trim();
    const e = empresa?.imagen_articulo?.trim();
    if (a) return `${process.env.REACT_APP_URL_BACKEND}${a}`;
    if (e) return `${process.env.REACT_APP_URL_BACKEND}${e}`;
    return logoDefault;
  }, [empresa]);

  const mapProducto = useCallback((producto) => { 
    const stockFormatted = producto?.stock != null
      ? Number.isInteger(producto.stock)
        ? producto.stock.toString()
        : producto.stock.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0, useGrouping: true })
      : "0";

    let textoPrecio;
    switch (producto?.tipoPrecioFinal) {
      case 2: textoPrecio = "Oferta"; break;
      case 1: textoPrecio = "Lista"; break;
      default: textoPrecio = "Disponible"; break;
    }

    /*const installment = (producto?.stock > 0 ? `${textoPrecio}: ${stockFormatted} u` : "Sin Stock")
     ;
*/
     const installment = (producto?.stock > 0 ? `${textoPrecio}` : "Sin Stock")
     ;

    const price = producto?.precioMostrar != null
      ? "$" + producto.precioMostrar.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : "Precio no disponible";

    const priceOld = producto?.precioVigente != null
      ? "$" + producto.precioVigente.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : null;

    const percentage = producto?.precioVigente > 0 && producto?.precioMostrar != null
      ? (((producto.precioVigente - producto.precioMostrar) / producto.precioVigente) * 100)
        .toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
      : null;

    const fractional= producto?.art_fraccionable === 1 ? true :false;
    
    return {
      id: producto.id, code: producto.art_codigo,
      category: producto?.Ramo?.descripcion_ramo ?? "Sin categoría",
      description: producto.art_descripcion, section: "TrendingProducts",
      imgSrc: getImageSrc(producto), altText: producto.art_descripcion,
      title: producto.art_descripcion ?? "Sin título",
      price, priceOld, percentage, stockActual: stockFormatted, installment,
      fractional,
      availability: producto?.art_desclarga ?? "", rating: null, reviews: 580,
    };
  }, [empresa, getImageSrc]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const cliente = JSON.parse(localStorage.getItem('clienteSelected'));
      const ord = getOrder(orderBy);
      let idcli = 1;

      if (user?.rol_id === 0) {
        idcli = user.id;
      } else if (cliente?.id) {
        idcli = cliente.id;
      }

      const response = await getArticulosByCategory({
        searchTerm,
        ramoId: selectedRamoId,       // ← ID para el backend
        rubroId: selectedRubroId,
        subrubroId: selectedSubrubroId,
        marcaId: selectedMarcaId,
        page: currentPage, perPage,
        user_id: idcli,
        orderBy: ord.orderBy, orden: ord.orden,
      });

      if (response) {
        setProducts(response.products.map(mapProducto));
        setTotalPages(response.pagination.totalPage);
        setTotalItems(response.pagination.totalItems);
      }
    } catch (e) {
      console.error("Error al obtener productos:", e);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedRamoId, selectedRubroId, selectedSubrubroId,
    selectedMarcaId, currentPage, perPage, orderBy, empresa, mapProducto]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  const fetchAllProductsForPdf = async () => {
    try {
      const cliente = user;
      const ord = getOrder(orderBy);
      const base = {
        searchTerm, ramoId: selectedRamoId, rubroId: selectedRubroId,
        subrubroId: selectedSubrubroId, marcaId: selectedMarcaId, userId: cliente?.id,
        orderBy: ord.orderBy, orden: ord.orden
      };

      const preview = await getArticulosByCategory({ ...base, page: 1, perPage: 1 });
      const total = preview?.pagination?.totalItems || 0;
      if (total === 0) return [];

      const full = await getArticulosByCategory({ ...base, page: 1, perPage: total });
      return full?.products?.map(mapProducto) ?? [];
    } catch (e) { console.error(e); return []; }
  };

  const handleOpenPdf = async (format) => {
    setGenerandoPDF(true);
    try {
      const ramoDesc = ramos.find(r => String(r.id) === String(selectedRamoId))?.descripcion_ramo ?? '';
      const rubroDesc = rubros.find(r => String(r.id) === String(selectedRubroId))?.descripcion_rubro ?? '';
      const subrubroDesc = subrubros.find(s => String(s.id) === String(selectedSubrubroId))?.descripcion_subrubro ?? '';
      const marcaDesc = marcas.find(m => String(m.id) === String(selectedMarcaId))?.mar_descripcion ?? '';
      const seccion = [ramoDesc, rubroDesc, subrubroDesc, marcaDesc, searchTerm].filter(Boolean).join(' / ');

      const allProducts = await fetchAllProductsForPdf();
      const blob = await pdf(<ExportPdf products={allProducts} empresa={empresa} seccion={seccion} format={format} />).toBlob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) { console.error(e); }
    finally { setGenerandoPDF(false); }
  };

  const toggleCartItem = (artId) => {
    const idCarrito = localStorage.getItem('idCarrito');
    if (!idCarrito) return;
    const exists = cartItems.find(i => i.cartId === idCarrito && i.artId === artId);
    if (exists) setCartItems(p => p.filter(i => i.cartId !== idCarrito || i.artId !== artId));
    else setCartItems(p => [...p, { cartId: Number(idCarrito), artId }]);
  };

  if (loading) return <Loading message="Cargando artículos..." />;

  return (
    <Grid container spacing={2} p={2} alignItems="center">
      <CategoryFilter
        ramoId={selectedRamoId}
        setRamoId={setRamoId}       // ← prop name coincide con lo que usa el componente
        rubroId={selectedRubroId}
        setRubroId={setRubroId}
        subrubroId={selectedSubrubroId}
        setSubrubroId={setSubrubroId}
        marcaId={selectedMarcaId}
        setMarcaId={setMarcaId}
        clearAll={clearAll}
      />
      <Grid container spacing={2} sx={{ mb: 2 }} justifyContent="center">
        <ControlsRow
          isLogin={user?.id} products={products}
          generandoPDF={generandoPDF} setGenerandoPDF={setGenerandoPDF}
          handleOpenPdf={handleOpenPdf} viewMode={viewMode} setViewMode={setViewMode}
          orderBy={orderBy} setOrderBy={setOrderBy}
        />
      </Grid>
      <ProductList
        products={products} isCheckStock={empresa?.venta_sin_stock}
        viewMode={viewMode} totalPages={totalPages} currentPage={currentPage}
        perPage={perPage} totalItems={totalItems}
        handlePageChange={setCurrentPage} handlePerPageChange={setPerPage}
        cartItems={cartItems} toggleCartItem={toggleCartItem}
      />
    </Grid>
  );
};

export default CategoryResults;